import React from 'react';
import { Link } from 'react-router-dom';
import ProductCard from '../common/ProductCard';
import { Product, getProductCategory } from '../../types';
import { getProductPriceInfo } from '../../utils/pricing';

interface Props {
  title: string;
  subtitle?: string;
  products: Product[];
  viewAllLink?: string;
  className?: string;
  /**
   * When true, ingredient groups smaller than MIN_GROUP_SIZE are dropped
   * entirely instead of being rolled into an "Other" bucket. Useful when
   * the section intentionally only wants clean, on-message ingredient
   * families (e.g. homepage Weight & Diabetes section).
   */
  hideOrphans?: boolean;
}

const extractIngredientLabel = (name: string): string => {
  if (!name) return 'Other';
  const m = name.match(/\(([^)]+)\)/);
  if (!m) return 'Other';
  let label = m[1]
    .split(',')[0]
    .replace(/\s*\d+(\.\d+)?\s*(mg|mcg|g|%|ml|iu)\b.*/gi, '')
    .trim();
  // Strip salt-form suffixes so "Sildenafil Citrate" groups with "Sildenafil"
  label = label.replace(/\s+(Citrate|Hydrochloride|HCl|Sodium|Sulfate|Besylate|Maleate|Tartrate)\s*$/i, '').trim();
  // Strip administration-route prefix so "Oral Minoxidil" groups with "Minoxidil"
  label = label.replace(/^(Oral|Topical|Injectable|Inhaled)\s+/i, '').trim();
  return label || 'Other';
};

const IngredientGroupedSection: React.FC<Props> = ({
  title,
  subtitle,
  products,
  viewAllLink = '/shop',
  className = '',
  hideOrphans = false,
}) => {
  if (products.length === 0) return null;

  // Group products by ingredient (case-insensitive key, original-case display label)
  const groupMap = new Map<string, { label: string; items: Product[] }>();
  products.forEach(p => {
    const label = extractIngredientLabel(p.name || '');
    const key = label.toLowerCase();
    const grp = groupMap.get(key) || { label, items: [] };
    grp.items.push(p);
    groupMap.set(key, grp);
  });

  const allGroups = Array.from(groupMap.values())
    .sort((a, b) => b.items.length - a.items.length || a.label.localeCompare(b.label));

  // Groups smaller than 3 get merged into a single "Other" bucket: a 2-item
  // row in a 4-col grid leaves half the row empty, which reads as ragged.
  // 3-item rows only leave 1 cell empty — acceptable density.
  const MIN_GROUP_SIZE = 3;
  const bigGroups = allGroups.filter(g => g.items.length >= MIN_GROUP_SIZE);
  const orphans = allGroups
    .filter(g => g.items.length < MIN_GROUP_SIZE)
    .flatMap(g => g.items);
  const sortedGroups = hideOrphans
    ? bigGroups
    : (orphans.length > 0
        ? [...bigGroups, { label: 'Other', items: orphans }]
        : bigGroups);
  if (sortedGroups.length === 0) return null;

  return (
    <section className={`py-20 lg:py-28 ${className}`}>
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12">
        <div className="flex items-end justify-between mb-12 lg:mb-16">
          <div>
            <h2 className="font-display text-display-sm lg:text-display-md text-forest-900 tracking-tight">{title}</h2>
            {subtitle && (
              <p className="text-body text-warm-500 mt-3 tracking-wide">{subtitle}</p>
            )}
          </div>
          <Link
            to={viewAllLink}
            className="hidden sm:inline-flex items-center gap-2 text-[14px] font-medium text-warm-500 hover:text-forest-800 transition-colors duration-300 group"
          >
            View all
            <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          </Link>
        </div>

        <div className="space-y-14 lg:space-y-20">
          {sortedGroups.map(group => {
            // Pad to the next multiple of 4 (largest grid cols) so rows are flush.
            // Trailing empty cells get filled with "View all →" cards pointing to the
            // section's /shop page — preserves density without cutting curated items.
            const COLS = 4;
            const padCount = group.items.length >= COLS
              ? (COLS - (group.items.length % COLS)) % COLS
              : COLS - group.items.length;
            return (
              <div key={group.label}>
                {sortedGroups.length > 1 && (
                  <h3 className="font-display text-heading-md text-forest-800 tracking-tight mb-6 flex items-center gap-4">
                    <span>{group.label}</span>
                    <span className="inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full bg-warm-100 text-warm-600 text-xs font-sans font-medium">
                      {group.items.length}
                    </span>
                  </h3>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-5">
                  {group.items.map(product => {
                    const priceInfo = getProductPriceInfo(product);
                    return (
                      <ProductCard
                        key={product.id}
                        id={product.id}
                        category={getProductCategory(product)}
                        name={product.name || ''}
                        originalPrice={priceInfo.originalPrice}
                        discount={priceInfo.discount}
                        finalPrice={priceInfo.finalPrice}
                        perPill={priceInfo.perPill}
                        quantity={priceInfo.quantity}
                        manufacturer={product.manufacturer || ''}
                        rating={product.rating || 0}
                        reviewCount={product.review_count || 0}
                        image={product.image || undefined}
                      />
                    );
                  })}
                  {Array.from({ length: padCount }).map((_, idx) => (
                    <Link
                      key={`pad-${idx}`}
                      to={viewAllLink}
                      className="group flex flex-col rounded-xl overflow-hidden transition-all duration-300"
                    >
                      <div className="relative aspect-square rounded-xl overflow-hidden mb-4 bg-cream-50 border-2 border-dashed border-warm-200 flex flex-col items-center justify-center transition-colors group-hover:bg-cream-100 group-hover:border-warm-300">
                        <svg className="w-10 h-10 text-warm-400 group-hover:text-forest-600 transition-all duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" strokeWidth={1.25} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                        </svg>
                        <span className="mt-3 text-sm font-medium text-warm-500 group-hover:text-forest-800 transition-colors tracking-wide">
                          View all
                        </span>
                      </div>
                      <div className="px-1">
                        <div className="text-[10px] uppercase tracking-wider text-warm-400 mb-1">
                          {sortedGroups.length > 1 ? group.label : title}
                        </div>
                        <div className="text-xs text-warm-500 line-clamp-2 leading-snug">
                          Browse the full range →
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="sm:hidden mt-10 text-center">
          <Link to={viewAllLink} className="btn-secondary text-sm inline-flex">
            View All Products
          </Link>
        </div>
      </div>
    </section>
  );
};

export default IngredientGroupedSection;
